import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, CheckCircle, Clock, Users, Award, Shield,
  Sparkles, TrendingUp, Target, Zap, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

// Certification Tiers Data
const TIERS = [
  {
    name: "Aspiring",
    tagline: "Begin Your Journey",
    description: "Designed for organisations beginning their conscious business journey. Focus on establishing baseline practices and identifying quick wins.",
    badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/tx2ugvu9_aspiring.jpeg",
    color: "#94A3B8",
    bgColor: "#F1F5F9",
    features: [
      "Baseline assessment across six pillars",
      "Quick wins identification report",
      "Foundation building roadmap",
      "Basic CNESS badge",
      "Directory listing",
      "Community access"
    ],
    ideal: "Startups and organisations new to conscious business practices",
    cta: "Start as Aspiring"
  },
  {
    name: "Inspired",
    tagline: "Commit to Excellence",
    description: "For companies committed to implementing improvements across all six pillars. Includes detailed coaching and interim recognition.",
    badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/ko1qopap_inspired.jpeg",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    features: [
      "Comprehensive evaluation",
      "Detailed coaching sessions",
      "Interim recognition certificate",
      "Priority directory placement",
      "Partner networking events",
      "Marketing co-branding",
      "Annual progress reviews"
    ],
    ideal: "Growing enterprises committed to continuous improvement",
    cta: "Get Inspired"
  },
  {
    name: "Luminary",
    tagline: "Lead the Movement",
    description: "Reserved for leaders who exemplify conscious business. Recognised globally and invited to contribute to the CNESS thought-leadership community.",
    badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/td0i3ea7_luminary.jpeg",
    color: "#FDB813",
    bgColor: "#FEF3C7",
    features: [
      "Global recognition & premium badge",
      "Thought-leadership opportunities",
      "Executive networking access",
      "Featured case studies",
      "Speaking opportunities",
      "Advisory board eligibility",
      "Premium marketing support",
      "Annual strategic review"
    ],
    ideal: "Industry leaders exemplifying conscious business excellence",
    cta: "Become a Luminary"
  }
];

// Success Metrics
const METRICS = [
  { value: "97%", label: "Success Rate", desc: "of organisations complete certification", icon: CheckCircle },
  { value: "30", label: "Days Average", desc: "from start to certification", icon: Clock },
  { value: "500+", label: "Champions", desc: "certified organisations globally", icon: Users },
  { value: "3x", label: "Revenue Growth", desc: "reported by certified companies", icon: TrendingUp }
];

const NewCertificationPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#F8FAFC] to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Award className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">CNESS Certification</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Transform Your Business Into a
              <span className="block text-[#FDB813]">Certified Conscious Enterprise</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8">
              Certification is not just a badge; it is a structured system to help companies 
              assess, align, advance and communicate better business practices.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => navigate('/enquiries')}
                data-testid="cert-hero-cta"
                className="w-full sm:w-auto px-8 py-4 text-lg bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl"
              >
                Start Your Certification
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/framework')}
                className="w-full sm:w-auto px-8 py-4 text-lg border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl"
              >
                View Six Pillars Framework
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-12 bg-gradient-to-r from-[#1E3A5F] to-[#2563EB]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {METRICS.map((metric, index) => (
              <div key={index} className="text-center">
                <metric.icon className="w-8 h-8 text-[#FDB813] mx-auto mb-3" />
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{metric.value}</div>
                <div className="text-white font-medium">{metric.label}</div>
                <div className="text-sm text-white/60">{metric.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              Your Certification Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A structured three-step process designed to guide you from assessment to achievement.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[
              { 
                step: "01", 
                title: "Assess", 
                icon: Target,
                desc: "Understand how your organisation currently performs across conscious enterprise practices, from purpose and governance to people, sustainability and impact.",
                details: ["Six-pillar evaluation", "Gap analysis", "Benchmark report"]
              },
              { 
                step: "02", 
                title: "Align", 
                icon: Users,
                desc: "Bring leadership, teams and operating practices around a shared framework so responsible business is embedded into how the company actually works.",
                details: ["Leadership alignment", "Team engagement", "Process integration"]
              },
              { 
                step: "03", 
                title: "Advance", 
                icon: TrendingUp,
                desc: "Use certification insights to prioritise improvements, strengthen trust and show measurable progress over time.",
                details: ["Continuous improvement", "Trust building", "Impact measurement"]
              }
            ].map((item, index) => (
              <div key={index} className="relative bg-[#F8FAFC] p-8 rounded-2xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#1E3A5F] rounded-xl flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-4xl font-bold text-[#FDB813]/30">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-4">{item.desc}</p>
                <ul className="space-y-2">
                  {item.details.map((detail, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {detail}
                    </li>
                  ))}
                </ul>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#FDB813] z-10" />
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-gray-500 italic">
              The certification journey typically takes a matter of weeks, not months, thanks to CNESS's structured approach.
            </p>
          </div>
        </div>
      </section>

      {/* Tiers Section */}
      <section id="tiers" className="py-20 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              Certification Tiers
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the tier that matches your organisation's stage and aspirations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {TIERS.map((tier, index) => (
              <div 
                key={index}
                className="relative bg-white rounded-3xl overflow-hidden transition-all hover:shadow-xl border border-gray-100 hover:border-[#FDB813]/40"
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <img 
                      src={tier.badge} 
                      alt={`${tier.name} CNESS Tier Badge`} 
                      className="w-24 h-24 object-contain"
                      data-testid={`tier-badge-${tier.name.toLowerCase()}`}
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F] mb-1">{tier.name}</h3>
                  <p className="text-sm font-medium mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                  <p className="text-gray-600 mb-6">{tier.description}</p>
                  
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 mb-2">Ideal for:</p>
                    <p className="text-sm font-medium text-[#1E3A5F]">{tier.ideal}</p>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start text-sm">
                        <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" style={{ color: tier.color }} />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => navigate(`/enquiries?tier=${encodeURIComponent(tier.name)}`)}
                    data-testid={`tier-cta-${tier.name.toLowerCase()}`}
                    className="w-full py-3 rounded-xl"
                    style={{ 
                      backgroundColor: '#1E3A5F',
                      color: 'white'
                    }}
                  >
                    {tier.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-500 mb-4">Need help choosing the right tier?</p>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact')}
              className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white px-8 py-3 rounded-xl"
            >
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-6">
                Why Get CNESS Certified?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                CNESS certification positions your organisation as a leader in conscious business, 
                providing tangible benefits that drive growth and stakeholder trust.
              </p>
              
              <div className="space-y-6">
                {[
                  { icon: Award, title: "Enhanced Brand Value", desc: "Stand out as a verified conscious enterprise with credible external validation." },
                  { icon: Shield, title: "Stakeholder Trust", desc: "Give customers, employees, and investors confidence in your practices." },
                  { icon: TrendingUp, title: "Competitive Advantage", desc: "Attract talent, customers, and partners who prioritise values alignment." },
                  { icon: Zap, title: "Operational Excellence", desc: "Identify improvements that enhance both impact and performance." }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-[#FDB813]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1E3A5F] mb-1">{benefit.title}</h4>
                      <p className="text-gray-600">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">By the Numbers</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <span className="text-white/70">Consumer trust drives purchases</span>
                  <span className="text-2xl font-bold text-[#FDB813]">81%</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <span className="text-white/70">Buyers pay more for trusted brands</span>
                  <span className="text-2xl font-bold text-[#FDB813]">87%</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <span className="text-white/70">Gen Z seek values-aligned brands</span>
                  <span className="text-2xl font-bold text-[#FDB813]">70%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Millennials prefer purpose-driven orgs</span>
                  <span className="text-2xl font-bold text-[#FDB813]">73%</span>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-6">
                Sources: Edelman Trust Barometer, Exploding Topics Research
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-6">
            Ready to Begin Your Certification Journey?
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Join hundreds of organisations that have transformed their practices and 
            earned the trust of their stakeholders through CNESS certification.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/enquiries')}
              data-testid="cert-bottom-cta"
              className="w-full sm:w-auto px-8 py-4 text-lg bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl"
            >
              Start Your Certification
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact')}
              className="w-full sm:w-auto px-8 py-4 text-lg border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl"
            >
              Request Pricing
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default NewCertificationPage;
